import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';

import { CollectionType } from '@/enums/collection-type';
import { TitleListLayout } from '@/enums/title-list-layout';

import type { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import type { PopularTitleGraphqlFragment } from '@/pages/graphql/fragments/PopularTitle.fragment';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';

import { getActiveFilters } from '@/helpers/filter-helper';
import { getDefaultFilterCollection, type FilterCollection } from '@/stores/useFilterStoreTypes';

import type TitleListGridType from '@/components/list/TitleListGrid.vue';
import { LoadMorePayload } from '@/helpers/composables/useBiScroll';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleList',
  props: {
    titlesAmount: null,
    collectionType: null,
    titles: null,
    titlesPerPage: null,
    listLayout: null,
    isAtEndOfList: { type: Boolean, default: false },
    isFirstPage: { type: Boolean, default: true },
    isSeoSRLoaded: { type: Boolean, default: false },
    hasActiveFilter: { type: Boolean, default: false },
    currentFilters: { default: getDefaultFilterCollection },
    sponsoredAd: { default: undefined },
    paginationLevel: { default: 0 },
    loading: { type: Boolean, default: false },
    placement: null
  },
  emits: ["onPosterClick", "removeTitleFromList", "load-next", "load-prev", "onScrollToTop", "show-fallbacks"],
  setup(__props: any, { expose }: { emit: ({
	(e: 'onPosterClick', payload: TitleEventPayloadGraphql): void;
	(e: 'removeTitleFromList', titleObjectId: number): void;
	(e: 'load-next', payload: LoadMorePayload): void;
	(e: 'load-prev', payload: LoadMorePayload): void;
	(e: 'onScrollToTop'): void;
	(e: 'show-fallbacks'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { titlesAmount: number, collectionType: CollectionType, titles: PopularTitleGraphqlFragment[], titlesPerPage?: number, listLayout: TitleListLayout, isAtEndOfList: boolean, isFirstPage: boolean, isSeoSRLoaded: boolean, hasActiveFilter: boolean, currentFilters: FilterCollection, sponsoredAd: SponsoredAdFragment | undefined, paginationLevel: 0 | 1 | 2, loading: boolean, placement?: ClickoutPlacement };

const FallbackTitles = () =>
	import(/* webpackChunkName: "timeline-fallback-titles" */ '@/components/timeline/FallbackTitles.vue');
const TimelineEmptyState = () =>
	import(/* webpackChunkName: "timeline-empty-state" */ '@/components/timeline/TimelineEmptyState.vue');
const TitleListRow = () => import(/* webpackChunkName: "title-list-row" */ '@/components/list/TitleListRow.vue');
const TitleListGrid = () => import(/* webpackChunkName: "title-list-grid" */ '@/components/list/TitleListGrid.vue');
const TitleListCard = () => import(/* webpackChunkName: "title-list-card" */ '@/components/list/TitleListCard.vue');



useProvideClickoutPlacement(props.placement);



const hasNoTitles = computed<boolean>(() => (props.titles.length || props.titlesAmount) === 0);

const showEmptyStateContent = computed<boolean>(() => {
	if (props.collectionType === CollectionType.TITLELIST) return false;
	if (props.hasActiveFilter) return true;

	const isTimelinePage = [CollectionType.NEW, CollectionType.POPULAR].includes(props.collectionType);

	// isAtEndOfTimeline
	return isTimelinePage && (props.isAtEndOfList || hasNoTitles.value);
});

const listLayoutProps = computed(() => {
	return {
		hasNoTitles: hasNoTitles.value,
		isAtEndOfList: props.isAtEndOfList,
		isFirstPage: props.isFirstPage,
		isSeoSRLoaded: props.isSeoSRLoaded,
		listLayout: props.listLayout,
		loading: props.loading,
		sponsoredAd: props.sponsoredAd,
		titles: props.titles,
		titlesAmount: props.titlesAmount,
		titlesPerPage: props.titlesPerPage,
		collectionType: props.collectionType,
		paginationLevel: props.paginationLevel,
	};
});

const collectionTypeToListLayout = {
	[TitleListLayout.ROW]: TitleListRow,
	[TitleListLayout.GRID]: TitleListGrid,
	[TitleListLayout.CARD]: TitleListCard,
};
const listLayoutComponent = computed(() => collectionTypeToListLayout[props.listLayout]);

const showFallbackTitles = computed(() => {
	const activeFilters = getActiveFilters(Object.keys(props.currentFilters) as any, props.currentFilters);

	return (
		activeFilters.providers &&
		Object.keys(activeFilters).every(filter => ['content_types', 'providers'].includes(filter)) &&
		hasNoTitles.value
	);
});

const titleList = ref<InstanceType<typeof TitleListGridType> | null>(null);

function resetInfiniteScroll() {
	titleList.value?.resetInfiniteScroll();
}

expose({ resetInfiniteScroll });

return { __sfc: true,FallbackTitles, TimelineEmptyState, TitleListRow, TitleListGrid, TitleListCard, props, hasNoTitles, showEmptyStateContent, listLayoutProps, collectionTypeToListLayout, listLayoutComponent, showFallbackTitles, titleList, resetInfiniteScroll }
}

})